/**
 * The title sting, as a pure function of where the menu theme has reached.
 *
 * See docs/intro-sequence.md. The two cue points below are measured off
 * `menu_2.mp3` rather than chosen: the track has a riser, a hit, a breath and
 * a drop, and the intro exists to land the logo on the first and the menu on
 * the last.
 */

export const INTRO_CUES = {
  /**
   * The breath. The hit at 9.50s and the fill after it are the loud part; the
   * track then drops back to about -16 dB for a quarter second before the
   * loop starts.
   *
   * The logo was originally cued to the hit, which read as a beat too early:
   * the title arrived while the fill was still going and had nothing left to
   * arrive *into*. Landing it in the quiet instead means the drop is the next
   * thing that happens, and the menu comes in under a title already on screen.
   */
  logoRevealMs: 10_500,

  /**
   * The drop. The fill runs to 10.45s, the track takes a breath down to
   * -16 dB, and the loop proper starts at 10.75s and stays loud. Opening the
   * menu on this instant is the whole point of the sequence.
   */
  menuHandoffMs: 10_750,
} as const;

/**
 * How far behind the frame clock the music may fall before the frame clock
 * takes over.
 *
 * Audio needs a moment to decode, so the track legitimately trails the scene
 * at the start. What it must never do is trail for ever: a refused or broken
 * track would otherwise strand the player on the title card with no skip to
 * escape through.
 */
export const MUSIC_STALL_GRACE_MS = 1_500;

export interface IntroCue {
  showLogo: boolean;
  finished: boolean;
}

/** What should be on screen once the sequence has reached `positionMs`. */
export function introCue(positionMs: number): IntroCue {
  return {
    showLogo: positionMs >= INTRO_CUES.logoRevealMs,
    finished: positionMs >= INTRO_CUES.menuHandoffMs,
  };
}

/**
 * Reconciles the audio clock with the frame clock.
 *
 * Phaser advances on `requestAnimationFrame` and Howler on the sound card;
 * over ten seconds they disagree, and a handoff 200ms late lands after the
 * drop instead of on it. So the music leads — except that it can never lead by
 * being stuck, hence the grace.
 */
export function introPositionMs(
  musicPositionMs: number | null,
  elapsedMs: number,
): number {
  if (musicPositionMs === null) {
    return elapsedMs;
  }
  return Math.max(musicPositionMs, elapsedMs - MUSIC_STALL_GRACE_MS);
}
